import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, CheckCircle, RotateCcw } from 'lucide-react';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import GenericSelect from '../../components/ui/GenericSelect';
import {
  Container,
  StickyHeader,
  Title,
  Card,
  DropZone,
  DropText,
  FileName,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  Footer,
  HistoryTable,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getLeaveImportTemplate,
  validateLeaveImport,
  executeLeaveImport,
} from '../../services/leaveImportService';
import { getLeaveVariations } from '../../services/leaveVariationService';

// Amir "Ekip İçin Toplu İzin Girişi (Excel)" sihirbazı. Etkileşim dili
// ImportPage.js / AssetImportPage.js'ten (dosya seçici, önizleme özeti, buton
// yerleşimi) taşındı — bkz. task-6-brief.md. Bu akış onlardan daha yalındır:
// backend'de status/history uç noktası yok (execute senkron döner), bu yüzden
// çok adımlı StepIndicator burada YOK; tek ekran: tür seç → şablon indir →
// dosya yükle (seçilince otomatik doğrulanır) → önizleme → kaydet.
//
// KRİTİK: execute, istemciden gönderilen satırları VERİ olarak alır, karar
// olarak değil — sunucu isValid/cost'u yok sayıp doğrulamayı sıfırdan koşar.
// `result` state'i her zaman EN SON sunucu yanıtını tutar (validate ya da
// execute fark etmez); önizleme/tablo hep `result`tan okunur, hiçbir zaman
// istemcinin yüklediği satırlardan değil.
// Satır hatası anahtarları iki kaynaktan gelir: LeaveImportRowParser/ProcessAsync'in
// kendi biçim hataları (`leaveImport.error.*`, kök namespace'te iç içe tanımlı) VE
// LeaveDataAccess.Validate'ten aynen aktarılan `leave.*` anahtarları (yalnız
// `responseErrors` altında düz anahtar olarak var, kökte YOK). Önce responseErrors'a
// bak, yoksa kök namespace'e düş, o da yoksa ham anahtarı göster.
function translateRowError(t, key) {
  return t(`responseErrors.${key}`, { defaultValue: t(key, { defaultValue: key }) });
}

export default function LeaveImportPage() {
  const { t } = useTranslation();
  const [variations, setVariations] = useState([]);
  const [variationId, setVariationId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const res = await getLeaveVariations();
      // api.js GET okumalarında reject ETMEZ — gerçek başarı sinyali res.data.status.
      if (!res?.data?.status) return;
      const list = res.data.data?.data ?? res.data.data ?? [];
      setVariations(list);
      // Varsayılan: adı "Yıllık" içeren ilk varyasyon, yoksa listenin ilki.
      const yillik = list.find((v) => (v.name || '').toLocaleLowerCase('tr').includes('yıllık'));
      setVariationId((yillik ?? list[0])?.id ?? '');
    })();
  }, []);

  const validRows = result?.validRows ?? 0;
  const errorRows = result?.errorRows ?? 0;
  const rows = useMemo(() => result?.rows ?? [], [result]);
  const inputDisabled = !variationId || busy;

  const handleVariationChange = useCallback((id) => {
    setVariationId(id);
    // Önceki tür altında doğrulanmış satırlar yeni türde geçersiz olabilir —
    // yanlış türe kaydetmeyi önlemek için önizlemeyi sıfırla.
    setResult(null);
    setFileName('');
    setSaved(null);
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    const res = await getLeaveImportTemplate();
    // GET okumaları api.js'te asla reject ETMEZ — 403/hata durumunda res.data
    // EMPTY_RESPONSE'un gövdesi ({status:false,data:null}) olur, gerçek bir Blob
    // DEĞİL. Denetlemeden new Blob([...])'a sarılırsa tarayıcı birkaç baytlık
    // bozuk bir .xlsx indirir. Gerçek bir xlsx zip overhead'i yüzünden her zaman
    // birkaç KB'tır; 100 bayt eşiği bunu güvenle ayırt eder.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flexbee-izin-import-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileChosen = useCallback(async (file) => {
    if (!file || !file.name.endsWith('.xlsx')) return;
    setFileName(file.name);
    setSaved(null);
    setBusy(true);
    try {
      // POST — api.js interceptor REJECT eder (mutasyon); hata zaten interceptor'ın
      // toast'ıyla gösteriliyor, burada yakalamamızın tek amacı `finally`'nin
      // busy'yi geri açmasını garanti etmek (aksi halde bozuk bir dosyada yükleme
      // kalıcı olarak pasif kalır — yeniden dosya seçilemez).
      const res = await validateLeaveImport(file, variationId);
      if (res?.data?.status) setResult(res.data.data);
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [variationId]);

  const handleInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    handleFileChosen(file);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
  }, [handleFileChosen]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (inputDisabled) return;
    handleFileChosen(e.dataTransfer.files?.[0]);
  }, [handleFileChosen, inputDisabled]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    try {
      const res = await executeLeaveImport(variationId, rows);
      // Sonuç ekranı execute'un DÖNDÜĞÜ satırları gösterir — önizlemede yeşil
      // görünen bir satır sunucuda reddedilmiş olabilir.
      if (res?.data?.status) {
        setResult(res.data.data);
        setSaved(res.data.data.validRows ?? 0);
      }
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [variationId, rows]);

  const handleReset = useCallback(() => {
    setResult(null);
    setFileName('');
    setSaved(null);
  }, []);

  return (
    <Container>
      <StickyHeader>
        <Title>{t('leaveImport.title')}</Title>
      </StickyHeader>

      <Card>
        <SectionTitle>{t('leaveImport.selectType')}</SectionTitle>
        <div style={{ maxWidth: 320 }}>
          <GenericSelect
            label={t('leaveImport.variationLabel')}
            options={variations}
            value={variationId}
            onChange={handleVariationChange}
            idKey="id"
            labelKey="name"
          />
        </div>
        {/* Burada seçilen tür artık VARSAYILANDIR: dosyanın "İzin Türü"
            kolonunu dolduran satırlar kendi türüne yazılır. */}
        <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085', marginTop: 8 }}>
          {t('leaveImport.variationHint')}
        </p>

        <Footer>
          <Button variant="outline" onClick={handleDownloadTemplate} disabled={!variationId}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('leaveImport.downloadTemplate')}
          </Button>
          <div />
        </Footer>
      </Card>

      <Card>
        <SectionTitle>{t('leaveImport.uploadFile')}</SectionTitle>

        <DropZone
          $dragOver={dragOver}
          onClick={() => { if (!inputDisabled) fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!inputDisabled) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={inputDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Upload size={32} color="#667085" />
          <DropText>
            {fileName ? <FileName>{fileName}</FileName> : t('leaveImport.dropzone')}
          </DropText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            data-testid="leave-import-file"
            disabled={inputDisabled}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </DropZone>

        {result && (
          <>
            <SummaryGrid style={{ marginTop: 20 }}>
              <SummaryCard $color="#F0FDF4">
                <SummaryValue>{validRows}</SummaryValue>
                <SummaryLabel>{t('leaveImport.willBeSaved')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#FEF2F2">
                <SummaryValue>{errorRows}</SummaryValue>
                <SummaryLabel>{t('leaveImport.hasError')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <HistoryTable>
              <thead>
                <tr>
                  <th>{t('leaveImport.rowNumber')}</th>
                  <th>{t('employee.registrationNumber')}</th>
                  <th>{t('employee.fullName')}</th>
                  <th>{t('leaveImport.variationColumn')}</th>
                  <th>{t('leave.leaveStart')}</th>
                  <th>{t('leave.leaveEnd')}</th>
                  <th>{t('leaveImport.calculatedDays')}</th>
                  <th>{t('leaveImport.error.title')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.rowNumber}
                    style={!r.isValid ? { background: '#FEF2F2' } : undefined}
                  >
                    <td>{r.rowNumber}</td>
                    <td>{r.registrationNumber}</td>
                    <td>{r.fullName}</td>
                    {/* Sunucunun ÇÖZDÜĞÜ tür gösterilir; satırda tür yazmıyorsa
                        yukarıdaki seçime düşer. Hatalı satırda boş kalabilir. */}
                    <td>{r.resolvedLeaveVariationName ?? '-'}</td>
                    <td>{r.leaveStart ? new Date(r.leaveStart).toLocaleDateString() : '-'}</td>
                    <td>{r.leaveEnd ? new Date(r.leaveEnd).toLocaleDateString() : '-'}</td>
                    <td>{r.cost ?? '-'}</td>
                    <td>{r.errorKey ? translateRowError(t, r.errorKey) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </>
        )}

        <Footer>
          <div>
            {saved !== null && (
              <Tooltip text={t('leaveImport.resetTooltip')}>
                <IconButton onClick={handleReset}>
                  <RotateCcw size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          {saved === null && (
            <Button
              data-testid="leave-import-submit"
              disabled={validRows === 0 || busy}
              onClick={handleSave}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {`${t('leaveImport.save')} (${validRows})`}
            </Button>
          )}
        </Footer>

        {saved !== null && (
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginTop: 8 }}>
            <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {t('leaveImport.savedSummary', { count: saved })}
          </p>
        )}
      </Card>
    </Container>
  );
}
